import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DetailsModal from '../components/Details/DetailsModal';

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Number(value));
};

const Details = ({ user }) => {
    const [details, setDetails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [recalculating, setRecalculating] = useState(false);
    const [message, setMessage] = useState('');

    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 25,
        totalCount: 0,
        totalPages: 1
    });

    const [filters, setFilters] = useState({
        accountNumber: '',
        personName: ''
    });

    // Состояние для модального окна
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const loadDetails = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                pageSize: pagination.pageSize.toString(),
                ...filters
            });

            const response = await api.getDetails(params);
            const dataList = response.data?.data || response.data || [];
            const totalCount = response.data?.totalCount || 0;
            const totalPages = response.data?.totalPages || (dataList.length > 0 ? 1 : 0);

            setDetails(dataList);
            setPagination(prev => ({ ...prev, totalCount, totalPages }));
        } catch (error) {
            setMessage('Ошибка загрузки детализации');
            console.error('Load details error:', error);
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.pageSize, filters]);

    useEffect(() => {
        loadDetails();
    }, [loadDetails]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const clearFilters = () => {
        setFilters({ accountNumber: '', personName: '' });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const goToPage = (page) => {
        if (page >= 1 && page <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page }));
        }
    };

    const handleRecalculate = async () => {
        if (!window.confirm('Запустить пересчёт детализации?')) return;

        setRecalculating(true);
        setMessage('');
        try {
            await api.recalculateDetails();
            setMessage('Пересчёт выполнен успешно');
            await loadDetails();
        } catch (error) {
            setMessage('Ошибка при пересчёте');
            console.error('Recalculate error:', error);
        } finally {
            setRecalculating(false);
        }
    };

    // Обработчик двойного клика на строке таблицы
    const handleRowDoubleClick = (item) => {
        // Собираем все уникальные ФИО для данного счета
        const personNamesForAccount = details
            .filter(detail => detail.number === item.number)
            .map(detail => detail.personName)
            .filter((name, index, self) => name && self.indexOf(name) === index); // Убираем дубликаты

        setSelectedAccount({
            accountNumber: item.number,
            personNames: personNamesForAccount
        });
        setShowModal(true);
    };

    // Закрытие модального окна
    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedAccount(null);
    };

    const generatePageNumbers = () => {
        const pages = [];
        const current = pagination.page;
        const total = pagination.totalPages;

        if (total <= 7) {
            for (let i = 1; i <= total; i++) pages.push(i);
        } else {
            pages.push(1);
            if (current > 3) pages.push('...');
            const start = Math.max(2, current - 1);
            const end = Math.min(total - 1, current + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (current < total - 2) pages.push('...');
            pages.push(total);
        }

        return pages;
    };

    const renderPaginationControls = () => (
        <div className="pagination-controls">
            <button
                onClick={() => goToPage(1)}
                disabled={pagination.page === 1}
                className="btn-pagination btn-pagination-first"
            >
                ⟪
            </button>
            <button
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn-pagination btn-pagination-prev"
            >
                ⟨
            </button>

            {generatePageNumbers().map((pageNum, index) => (
                pageNum === '...' ? (
                    <span key={index} className="pagination-ellipsis">...</span>
                ) : (
                    <button
                        key={index}
                        onClick={() => goToPage(pageNum)}
                        className={`btn-pagination ${pagination.page === pageNum ? 'btn-pagination-active' : 'btn-pagination-inactive'}`}
                    >
                        {pageNum}
                    </button>
                )
            ))}

            <button
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages || pagination.totalPages === 0}
                className="btn-pagination btn-pagination-next"
            >
                ⟩
            </button>
            <button
                onClick={() => goToPage(pagination.totalPages)}
                disabled={pagination.page === pagination.totalPages || pagination.totalPages === 0}
                className="btn-pagination btn-pagination-last"
            >
                ⟫
            </button>
        </div>
    );

    return (
        <div className="page">
            <h2>Детализация</h2>

            {/* Кнопка пересчёта — только для admin и accountant */}
            {user && (user.role === 'admin' || user.role === 'accountant') && (
                <div className="page-header">
                    <button
                        onClick={handleRecalculate}
                        className="btn-primary"
                        disabled={recalculating}
                    >
                        {recalculating ? 'Пересчёт...' : 'Пересчитать'}
                    </button>
                </div>
            )}

            {message && (
                <div className={`message ${message.includes('Ошибка') ? 'error' : 'success'}`}>
                    {message}
                </div>
            )}

            {/* Панель фильтров */}
            <div className="filters-panel">
                <div className="filters-header">
                    <h4>Фильтры</h4>
                    <button
                        onClick={clearFilters}
                        className="btn-secondary btn-clear-filters"
                    >
                        Очистить фильтры
                    </button>
                </div>

                <div className="filters-grid">
                    <div className="filter-group number">
                        <label>№ счёта</label>
                        <input
                            type="text"
                            value={filters.accountNumber}
                            onChange={(e) => handleFilterChange('accountNumber', e.target.value)}
                            placeholder="По номеру счёта"
                        />
                    </div>

                    <div className="filter-group ownerName">
                        <label>ФИО</label>
                        <input
                            type="text"
                            value={filters.personName}
                            onChange={(e) => handleFilterChange('personName', e.target.value)}
                            placeholder="По ФИО"
                        />
                    </div>
                </div>

                <div className="filter-stats">
                    Всего записей: {pagination.totalCount}
                    {Object.values(filters).some(f => f !== '') && (
                        <span className="filters-applied">(применены фильтры)</span>
                    )}
                </div>
            </div>

            {/* Пагинация сверху */}
            <div className="pagination-top">
                <div className="pagination-info">
                    Страница {pagination.page} из {pagination.totalPages}
                    ({pagination.totalCount} всего записей)
                </div>
                {renderPaginationControls()}
            </div>

            {/* Таблица */}
            <div className="accounts-table-container">
                {loading && details.length === 0 ? (
                    <div className="loading">Загрузка...</div>
                ) : details.length === 0 ? (
                    <div className="no-data">
                        {Object.values(filters).some(f => f !== '')
                            ? 'Нет записей, соответствующих фильтрам'
                            : 'Нет данных для отображения'
                        }
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table className="accounts-table">
                            <thead>
                                <tr>
                                    <th rowSpan="2" style={{ textAlign: 'center', verticalAlign: 'middle', width: '90px' }}>
                                        № счёта
                                    </th>
                                    <th rowSpan="2" style={{ textAlign: 'center', verticalAlign: 'middle', minWidth: '180px' }}>
                                        ФИО
                                    </th>
                                    <th colSpan="4" style={{ textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                                        В запросах
                                    </th>
                                    <th colSpan="4" style={{ textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                                        Остаток
                                    </th>
                                </tr>
                                <tr>
                                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>Основной долг</th>
                                    <th style={{ textAlign: 'right' }}>Пени</th>
                                    <th style={{ textAlign: 'right' }}>Пошлина</th>
                                    <th style={{ textAlign: 'right' }}>Итого</th>
                                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>Основной долг</th>
                                    <th style={{ textAlign: 'right' }}>Пени</th>
                                    <th style={{ textAlign: 'right' }}>Пошлина</th>
                                    <th style={{ textAlign: 'right' }}>Итого</th>
                                </tr>
                            </thead>
                            <tbody>
                                {details.map((item, index) => (
                                    <tr
                                        key={item.id || index}
                                        className="account-row"
                                        onDoubleClick={() => handleRowDoubleClick(item)}
                                        style={{ cursor: 'pointer' }}
                                        title="Двойной клик для просмотра детализации"
                                    >
                                        <td className="account-number">{item.number}</td>
                                        <td>{item.personName}</td>
                                        <td className="number-cell">{formatCurrency(item.requestedMainDebt)}</td>
                                        <td className="number-cell">{formatCurrency(item.requestedPenalty)}</td>
                                        <td className="number-cell">{formatCurrency(item.requestedDuty)}</td>
                                        <td className="number-cell">{formatCurrency(item.requestedTotal)}</td>
                                        <td className="number-cell">{formatCurrency(item.balanceMainDebt)}</td>
                                        <td className="number-cell">{formatCurrency(item.balancePenalty)}</td>
                                        <td className="number-cell">{formatCurrency(item.balanceDuty)}</td>
                                        <td className="number-cell">{formatCurrency(item.balanceTotal)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Пагинация снизу */}
            <div className="pagination-bottom">
                <div className="pagination-info">
                    Страница {pagination.page} из {pagination.totalPages}
                    ({pagination.totalCount} всего записей)
                </div>
                {renderPaginationControls()}
            </div>

            {/* Модальное окно детализации */}
            {showModal && selectedAccount && (
                <DetailsModal
                    accountNumber={selectedAccount.accountNumber}
                    personNames={selectedAccount.personNames}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
};

export default Details;
